import { useEffect, useMemo, useState } from 'react';
import {
  Card, Select, Checkbox, Button, Table, Modal, Alert, Space, Typography, Tag, Empty
} from 'antd';
import { CopyOutlined, EyeOutlined, SwapRightOutlined } from '@ant-design/icons';
import { internalTenantClone } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';

const { Text, Paragraph } = Typography;

// Stage 5N-CLONE — replicate one tenant's configuration/pricing masters
// (Other Charges, fabrication process rates, glass types, routing, …) onto
// another tenant. super_admin-only. Replaces the target's config, so the
// flow is: pick source → target → sections → PREVIEW (row counts) → APPLY
// (confirm dialog). The backend runs it transactionally and refuses if the
// target already has operational data (CLONE_TARGET_IN_USE).

function tenantLabel(t) {
  const gstin = t.gstin ? ` · ${t.gstin}` : '';
  return `${t.displayName || 'Unnamed tenant'}${gstin}`;
}

export default function TenantConfigClonePage() {
  const { hasPermission } = useInternalAdminAuth();
  const { notifySuccess, notifyError } = useAppNotifications();
  const canClone = hasPermission('tenants.clone_config');

  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [sections, setSections] = useState([]);

  const [sourceId, setSourceId] = useState(null);
  const [targetId, setTargetId] = useState(null);
  const [selectedSections, setSelectedSections] = useState([]);

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ts, ss] = await Promise.all([
          internalTenantClone.listTenants(),
          internalTenantClone.listSections()
        ]);
        if (cancelled) return;
        setTenants(Array.isArray(ts) ? ts : []);
        setSections(Array.isArray(ss) ? ss : []);
        // Default: all sections ticked.
        setSelectedSections((Array.isArray(ss) ? ss : []).map((s) => s.id));
      } catch (err) {
        if (!cancelled) notifyError(err?.response?.data?.error?.message || 'Failed to load tenants.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [notifyError]);

  const tenantOptions = useMemo(
    () => tenants.map((t) => ({ value: t.tenantId, label: tenantLabel(t) })),
    [tenants]
  );
  const sourceTenant = useMemo(() => tenants.find((t) => t.tenantId === sourceId) || null, [tenants, sourceId]);
  const targetTenant = useMemo(() => tenants.find((t) => t.tenantId === targetId) || null, [tenants, targetId]);

  const sameTenant = sourceId && targetId && sourceId === targetId;
  const canRun = sourceId && targetId && !sameTenant && selectedSections.length > 0;

  // Any input change invalidates a stale preview.
  function resetPreview() { setPreview(null); }

  async function handlePreview() {
    if (!canRun) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const data = await internalTenantClone.preview({
        sourceTenantId: sourceId,
        targetTenantId: targetId,
        sections: selectedSections
      });
      setPreview(data);
    } catch (err) {
      notifyError(err?.response?.data?.error?.message || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  }

  async function handleApply() {
    setApplying(true);
    try {
      const res = await internalTenantClone.apply({
        sourceTenantId: sourceId,
        targetTenantId: targetId,
        sections: selectedSections
      });
      notifySuccess(
        `Cloned ${res.totalRows} rows across ${res.copied.length} tables into ${targetTenant?.displayName || 'target'}.`
      );
      setConfirmOpen(false);
      // Refresh preview so the "after" counts reflect reality.
      handlePreview();
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      const msg = err?.response?.data?.error?.message;
      if (code === 'CLONE_TARGET_IN_USE') {
        notifyError(msg || 'The target tenant already has operational data — clone aborted (nothing changed).');
      } else {
        notifyError(msg || 'Clone failed.');
      }
    } finally {
      setApplying(false);
    }
  }

  const previewRows = useMemo(() => {
    if (!preview) return [];
    const rows = [];
    preview.sections.forEach((sec) => {
      sec.tables.forEach((t) => {
        rows.push({ key: `${sec.id}.${t.table}`, section: sec.label, ...t });
      });
    });
    return rows;
  }, [preview]);

  const columns = [
    { title: 'Table', dataIndex: 'table', key: 'table', render: (v) => <Text code>{v}</Text> },
    { title: 'Section', dataIndex: 'section', key: 'section', responsive: ['lg'] },
    {
      title: 'In source', dataIndex: 'sourceRows', key: 'src', align: 'right',
      render: (v) => (v == null ? <Text type="secondary">—</Text> : v)
    },
    {
      title: 'In target (now)', dataIndex: 'targetRows', key: 'tgt', align: 'right',
      render: (v) => (v == null ? <Text type="secondary">—</Text> : v)
    },
    {
      title: 'After clone', key: 'after', align: 'right',
      render: (_, r) => (
        <Text strong style={{ color: '#1677ff' }}>
          {r.sourceRows == null ? '—' : r.sourceRows}
        </Text>
      )
    }
  ];

  if (loading) return <LoadingBlock label="Loading tenants…" />;

  if (!canClone) {
    return (
      <div>
        <PageHeader title="Clone Tenant Configuration" />
        <Alert
          type="warning"
          showIcon
          message="Not permitted"
          description="Cloning tenant configuration is restricted to super administrators."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Clone Tenant Configuration"
        subtitle="Replicate pricing, Other Charges and production masters from one tenant onto another"
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="What this does"
        description={
          <span>
            Copies the selected configuration masters from the <strong>source</strong> tenant into the{' '}
            <strong>target</strong> tenant, <strong>replacing</strong> the target's current values for those
            sections. It runs in one transaction and will refuse (changing nothing) if the target already has
            operational data such as work orders or proformas. It does not touch the item catalog or stock.
          </span>
        }
      />

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 280px', minWidth: 240 }}>
              <Text type="secondary">Source (copy FROM)</Text>
              <Select
                showSearch optionFilterProp="label"
                placeholder="Select source tenant"
                style={{ width: '100%', marginTop: 4 }}
                options={tenantOptions}
                value={sourceId}
                onChange={(v) => { setSourceId(v); resetPreview(); }}
              />
            </div>
            <SwapRightOutlined style={{ fontSize: 20, color: '#8c8c8c', paddingBottom: 6 }} />
            <div style={{ flex: '1 1 280px', minWidth: 240 }}>
              <Text type="secondary">Target (copy INTO — will be replaced)</Text>
              <Select
                showSearch optionFilterProp="label"
                placeholder="Select target tenant"
                style={{ width: '100%', marginTop: 4 }}
                options={tenantOptions}
                value={targetId}
                onChange={(v) => { setTargetId(v); resetPreview(); }}
                status={sameTenant ? 'error' : undefined}
              />
            </div>
          </div>

          {sameTenant && (
            <Alert type="error" showIcon message="Source and target must be different tenants." />
          )}

          <div>
            <Text type="secondary">Sections to copy</Text>
            <div style={{ marginTop: 8 }}>
              <Checkbox.Group
                value={selectedSections}
                onChange={(v) => { setSelectedSections(v); resetPreview(); }}
              >
                <Space direction="vertical">
                  {sections.map((s) => (
                    <Checkbox key={s.id} value={s.id}>
                      <Text strong>{s.label}</Text>{' '}
                      <Tag>{s.tables.length} tables</Tag>
                      <div style={{ marginLeft: 24 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{s.description}</Text>
                      </div>
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </div>
          </div>

          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={handlePreview}
              loading={previewing}
              disabled={!canRun}
            >
              Preview
            </Button>
            <Button
              type="primary" danger
              icon={<CopyOutlined />}
              onClick={() => setConfirmOpen(true)}
              disabled={!canRun || !preview}
            >
              Clone now
            </Button>
            {!preview && canRun && (
              <Text type="secondary">Run a preview before cloning.</Text>
            )}
          </Space>
        </Space>
      </Card>

      {preview && (
        <Card title="Preview — rows that will be written to the target">
          {previewRows.length ? (
            <Table
              size="small"
              columns={columns}
              dataSource={previewRows}
              pagination={false}
              rowKey="key"
              scroll={{ x: true }}
            />
          ) : (
            <Empty description="No tables in the selected sections" />
          )}
        </Card>
      )}

      <Modal
        title="Replace target configuration?"
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onOk={handleApply}
        okText="Yes, clone & replace"
        okButtonProps={{ danger: true, loading: applying, icon: <CopyOutlined /> }}
        cancelButtonProps={{ disabled: applying }}
      >
        <Paragraph>
          This will <strong>replace</strong> the selected configuration in{' '}
          <Tag color="volcano">{targetTenant?.displayName}</Tag> with the values from{' '}
          <Tag color="blue">{sourceTenant?.displayName}</Tag>.
        </Paragraph>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Sections: {selectedSections.join(', ')}. The catalog and stock are untouched. If the target already
          has work orders or proformas, the clone is refused and nothing changes.
        </Paragraph>
      </Modal>
    </div>
  );
}
